import React, { useCallback, useRef, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import TextareaAutosize from 'react-textarea-autosize';

import { ExtendedFile, InputChangeEvent } from '../types';
import { RootDispatch, RootState } from '../store';
import { isVwrFeatureEnabled } from '../util';
import { ReactComponent as AttachmentButtonSvg } from '../images/ma-plus-icon.svg';
import { trackMessageSent } from '../services/Tracker';
import { orgAllowAttachment, orgAllowPhoto } from '../selectors/organization';
import { useForm } from '../hooks';
import { getConnector } from '../services/PatientFacingClient';
import { AttachmentBanners } from './AttachmentBanners';

import styles from './ComposeArea.module.css';

const MAX_ATTACHED_FILES = 3;

export function ComposeArea({
  markConversationAsRead,
  allowAttachment,
  allowPhoto,
  sendMessage,
  startTyping,
  timelineRef,
}: {
  markConversationAsRead: (_payload: boolean) => unknown;
  allowAttachment: boolean;
  allowPhoto: boolean;
  sendMessage: ({ body, options }: { body: string; options: object }) => unknown;
  startTyping: (_payload: unknown) => void;
  timelineRef: React.RefObject<Element>;
}) {
  const [selectedFiles, setSelectedFiles] = useState<ExtendedFile[]>([]);
  const [acceptedFormatsString, setAcceptedFormatsString] = useState('');
  const composeAreaRef = useRef<HTMLDivElement>(document.createElement('div'));
  // if a message can't send, update the respective message bubble to reflect that.
  // blocking ui to wait until message succeeds makes typing successive messages feel unresponsive.
  const afterSubmit = async (
    inputs: { body: string; attachments: string },
    attachmentFiles: ExtendedFile | ExtendedFile[] | undefined | { attachments: unknown[] }
  ) => {
    await sendMessage({ body: inputs.body, options: { attachmentFiles } });

    if (isVwrFeatureEnabled()) {
      trackMessageSent('Virtual Waiting Room Provider');
    } else {
      trackMessageSent();
    }
    resetInputs();
    setSelectedFiles([]);
    // `refs` is likely mis-named and completely unused here. useForm returns `refs` as the
    // same type as `attachmentFiles`, but the usage here indicates it should be an HTMLElement.
    // This block can likely be removed, as it seemingly would never be called.
    // https://tigertext.atlassian.net/browse/TMG-853
    // @ts-expect-error
    if (refs && refs?.body) {
      // https://tigertext.atlassian.net/browse/TMG-853
      // @ts-expect-error
      refs.body.focus();
    }

    //eslint-disable-next-line scanjs-rules/call_setTimeout
    setTimeout(() => {
      timelineRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 120);
    await markConversationAsRead(true);
  };

  const { resetInputs, inputs, handleInputChange, refs, setRefs, resetInput } = useForm(
    { body: '', attachments: '' },
    afterSubmit
  );

  const hasDuplicatesInAttachments = (newFiles: File[], prevFiles: File[]): boolean => {
    const files = [...prevFiles, ...newFiles].map((f: File) => f.name);
    const uniqueFiles = new Set(files);
    return files.length !== uniqueFiles.size;
  };

  const convertArrayToFileList = (arr: File[]) => {
    const listOfFiles = new DataTransfer();
    arr.forEach((fileData: File) => {
      listOfFiles.items.add(fileData);
    });
    return listOfFiles.files;
  };

  const fileIsAcceptedFormat = (file: File) => {
    return acceptedFormatsString.includes(file.type);
  };

  const handleAttachmentsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const prevFiles = selectedFiles;
    // event.target.files could be null here
    // https://tigertext.atlassian.net/browse/TMG-853
    // @ts-expect-error
    let newFiles = Array.from(event.target.files) as ExtendedFile[];
    newFiles = newFiles.map((file) =>
      Object.assign(file, { unacceptedType: !fileIsAcceptedFormat(file) })
    );

    if (newFiles.length + prevFiles.length > MAX_ATTACHED_FILES) {
      alert(`
        The maximum number of files you can attach is ${MAX_ATTACHED_FILES}.
        Please remove attachment(s) and try again.
      `);
      newFiles = [];
    }

    if (hasDuplicatesInAttachments(newFiles, prevFiles)) {
      alert('You already have this file(s) attached to your message');
      newFiles = [];
    }

    const allFiles = [...prevFiles, ...newFiles];
    setSelectedFiles(allFiles);
    if (allFiles.length) {
      handleInputChange(event);
    }
  };

  const handleMessageSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    if (event) {
      event.preventDefault();
    }
    const acceptedFiles = selectedFiles.filter(fileIsAcceptedFormat);
    if (acceptedFiles.length > 1) {
      const newInputs = inputs;

      for (const fileData of acceptedFiles) {
        await afterSubmit(newInputs, fileData);
        if (newInputs.body) {
          newInputs.body = '';
        }
      }
      return;
    }

    await afterSubmit(inputs, acceptedFiles);
  };

  const removeAttachmentAtIndex = (index: number) => {
    if (selectedFiles.length > 1) {
      const attachedFiles = [...selectedFiles];
      attachedFiles.splice(index, 1);

      setSelectedFiles(attachedFiles);
      setRefs((refs) => ({
        ...refs,
        attachments: {
          ...(refs as { attachments: unknown[] }).attachments,
          files: convertArrayToFileList(attachedFiles),
        },
      }));
    } else {
      resetInput('attachments');
      setSelectedFiles([]);
    }
  };

  function handleTouchMove(e: TouchEvent) {
    e.preventDefault();
    return false;
  }

  const buildAcceptedFormats = useCallback(() => {
    const {
      client: {
        attachments: { allContentTypes = [] },
      },
    } = getConnector();

    setAcceptedFormatsString(
      allContentTypes
        .filter((e: string) => {
          if (allowAttachment && !allowPhoto)
            return !e.startsWith('image/') && !e.startsWith('video/');
          if (!allowAttachment && allowPhoto)
            return e.startsWith('image/') || e.startsWith('video/');
          return true;
        })
        .join(',')
    );
  }, [allowAttachment, allowPhoto, setAcceptedFormatsString]);

  useEffect(() => {
    composeAreaRef.current.addEventListener('touchmove', handleTouchMove);
    return () => composeAreaRef.current.removeEventListener('touchmove', handleTouchMove);
  }, []);

  useEffect(() => {
    buildAcceptedFormats();
  }, [allowAttachment, allowPhoto, buildAcceptedFormats]);

  const isSendable =
    inputs.body.trim() !== '' || selectedFiles.filter(fileIsAcceptedFormat).length > 0;
  const canSendAttachments = allowAttachment || allowPhoto;

  return (
    <div
      className={styles.composeArea}
      ref={(node) => (composeAreaRef.current = node as HTMLDivElement)}
    >
      {selectedFiles.length > 0 && (
        <AttachmentBanners
          attachments={selectedFiles}
          removeAttachmentAtIndex={removeAttachmentAtIndex}
        />
      )}
      <form onSubmit={handleMessageSubmit} className={styles.formArea}>
        {canSendAttachments && (
          <label id="attachmentButtonLabel" className={styles.attachmentButtonLabel}>
            <AttachmentButtonSvg />
            <input
              multiple
              accept={acceptedFormatsString}
              type="file"
              name="attachments"
              hidden={true}
              onChange={handleAttachmentsChange}
              value={inputs.attachments}
            />
          </label>
        )}
        <label className={styles.inputFormLabel}>
          <FormattedMessage id="compose.messageInput">
            {(placeholder: string | undefined) => (
              <TextareaAutosize
                placeholder={placeholder}
                className={styles.inputForm}
                name="body"
                maxRows={6}
                onFocus={() => markConversationAsRead(true)}
                onChange={(e: InputChangeEvent) => {
                  startTyping(null);
                  handleInputChange(e);
                }}
                value={inputs.body}
              />
            )}
          </FormattedMessage>
        </label>
        <button
          style={{ backgroundColor: isSendable ? '#489eb4' : '#e7e7e7' }}
          disabled={!isSendable}
          type="submit"
          aria-label="send-message-button"
          className={styles.submitButton}
        >
          <FormattedMessage id={`compose.sendButton`} />
        </button>
      </form>
    </div>
  );
}

const mapState = (state: RootState) => ({
  allowAttachment: orgAllowAttachment(state),
  allowPhoto: orgAllowPhoto(state),
});

const mapDispatch = ({
  conversation: { markConversationAsRead, startTyping },
  messages: { sendMessage },
}: RootDispatch) => ({
  markConversationAsRead,
  sendMessage,
  startTyping,
});

// https://tigertext.atlassian.net/browse/TMG-837
// @ts-expect-error
export default connect(mapState, mapDispatch)(ComposeArea);
